import { MatCalendarCellClassFunction, MatDatepickerToggle } from '@angular/material/datepicker'
import { WizValidatorFn, WizValidators, WizValidationErrors, WizValidationError } from './../../../../services/validators/wiz-validators';
import { WizNativeDateAdapter } from './../../../../services/date/wiz-native-date-adapter';
import { BASE_COMPONENT, WizBaseComponent } from './../../../core/wiz-base-component';
import {
  ElementRef, ViewChild, Component, forwardRef, ChangeDetectionStrategy, Input,
  ChangeDetectorRef, Renderer2, ViewContainerRef, AfterViewInit, OnDestroy,
  Output,
  EventEmitter
} from '@angular/core';
import { WizTranslatableComponent } from '../../../core/wiz-translatable-component';
import { DateAdapter } from '@angular/material/core';
import { WizFocusManager } from '../../../../services/core/focus/wiz-focus-manager';
import { WizMatDatepicker } from '../wiz-calendar/wiz-mat-datepicker';
import { IWIZ_COMPONENT} from '../../../../interfaces/iwiz-component';
import { FOCUS_MODE, VALUE_DOWN, VALUE_SPACE, VALUE_UP, WIZ_ERROR_MESSAGE, WIZ_EVENT_TYPE, WizDateUtils, WizStringUtils } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../../events/wiz-event';
import { WizEventManager } from '../../../../events/wiz-event.manager';
import { WizComponentTranslationObj } from '../../../../obj/translation/wiz-translation.obj';
import { WizTranslableProperty } from '../../../../services/translation/wiz-translatable-property.decorator';
import { WizTranslationManager } from '../../../../services/translation/wiz-translation.manager';

@Component({
  selector: 'wiz-datefield',
  templateUrl: './wiz-datefield.html',
  styleUrls: ['./wiz-datefield.scss'],
  providers: [
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizDateField) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizDateField) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizDateField) }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WizDateField extends WizTranslatableComponent implements AfterViewInit, OnDestroy {
  /** le datePicker (calendar en popUp) */
  // @ViewChild('datePicker', {static: true}) private datePicker: MatDatepicker<Date>;
  // accès au datePicker dez type WizMatDatepicker
  @ViewChild('datePicker', { static: true }) private datePicker: WizMatDatepicker<Date>;
  // hack du picker pour accéder au matCalendar, pour inscription comme un enfant (suivre la piste dans popOpened)
  @ViewChild('datePicker', { static: true }) private datePickerForPopDeclaration: ElementRef;


  /** ref du bouton toggle du datepicker */
  @ViewChild('datepickerToggle', { static: false }) private datepickerToggle: MatDatepickerToggle<any>;
  /** L'element ref de l'input (pour gestion du texte saisi), lu comme un élément de base pour atteindre le string */
  @ViewChild('dateInput', { static: true, read: ElementRef }) protected innerInput: ElementRef;
  /** Conteneur de message d'erreur */
  @ViewChild('errorContainer', { read: ViewContainerRef, static: true }) errorContainer: ViewContainerRef;

  /** Regexp utilisée pour saisie manuelle */
  private _dateRegExp: RegExp = new RegExp('(^\\d{1,4}'+'\/'+'{0,1}$)|(^\\d{1,2}'+'\/'+'{0,1}\\d{1,2}'+'\/'+'{0,1}\\d{0,4}$)', 'g');


/**
 * Constructeur
 * @param cdref: ChangeDetectorRef
 * @param elementRef: the dom of the component
 * @param adapter: date adapter service
 */
  constructor(
    public cdref: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2,
    private adapter: DateAdapter<Date>) {
    super(cdref, elementRef, renderer);
    this.setWizFocusMode(FOCUS_MODE.ROLLBACK);// pas de sortie en erreur sur un dateField (sauf contre-ordre)
  }

  /**
   * pour le datefield, détection d'une saisie pour activer les boutons save / delete
   */
  @Output()
  wizKey: EventEmitter<any> = new EventEmitter<any>();



  public ngAfterViewInit(): void { // liaison du dateFilter du picker à la filterFunc
    super.ngAfterViewInit();
    this.datePicker.datepickerInput.dateFilter = this.dateFilter;
    // key down pour les touches hors saisie > fermeture/ouverture avec les flèches
    WizEventManager.addListener(this, this.innerInput, 'keydown', (e: KeyboardEvent) => this.onKeyDown(e));
    // key press pour restriction de saisie
    WizEventManager.addListener(this, this.innerInput, 'keypress', (e: KeyboardEvent) => this.onKeyPress(e));
    // ecoute de l'evenement paste pour empécher de coller des caractères non valides
    WizEventManager.addListener(this, this.innerInput, 'paste', (e: ClipboardEvent) => this.onPaste(e));
  }

  /**
   * Suppression des écouteurs
   */
  public ngOnDestroy(): void { // liaison du dateFilter du picker à la filterFunc
    super.ngOnDestroy();
    WizEventManager.removeListener(this, this.innerInput, 'keydown');
    WizEventManager.removeListener(this, this.innerInput, 'keypress');
    WizEventManager.removeListener(this, this.innerInput, 'paste');
  }

  // ####
  // ####
  // #### configuration ####
  // ####
  // ####

  /** Mode année, mois ou jour */
  private _wizDateMode: string = WizDateUtils.DATE_MODE;
  @Input()
  public set wizDateMode(mode: string) {
    this.setWizDateMode(mode);
  }
  public get wizDateMode(): string {
    return this.getWizDateMode();
  }
  public setWizDateMode(mode: string): void {
    this._wizDateMode = mode;
  }
  public getWizDateMode(): string {
    return this._wizDateMode;
  }

  private _wizShowClearButton: boolean = true; // afficher le bouton "clear"
  @Input()
  public set wizShowClearButton(v: boolean) {
    this.setWizShowClearButton(v);
  }
  public get wizShowClearButton(): boolean {
    return this.getWizShowClearButton();
  }

  /**
   * methodes getter/setter surchargeables du WizShowClearButton
   */
  protected setWizShowClearButton(value: boolean): void {
    this._wizShowClearButton = value;
    this.cdref.markForCheck();
  }
  protected getWizShowClearButton(): boolean {
    return this._wizShowClearButton;
  }

  private _wizShowErrors: boolean = false; // afficher les erreurs sous le bouton
  // attention, le composant doit porter la directive WizValidator pour être pris en compte
  @Input()
  public set wizShowErrors(v: boolean) {
    this.setWizShowErrors(v);
  }
  public get wizShowErrors(): boolean {
    return this.getWizShowErrors();
  }

  /**
   * methodes getter/setter surchargeables du WizShowErrors
   */
  protected setWizShowErrors(value: boolean): void {
    this._wizShowErrors = value;
    this.cdref.markForCheck();
  }
  protected getWizShowErrors(): boolean {
    return this._wizShowErrors;
  }
  // prompt traduit, injecté depuis wizPlaceholder
  // translatedPlaceholder est initialisé par une chaine vide pour éviter d'afficher undefined
  public translatedPlaceholder: string = '';
  private _wizPlaceholder: string;
  @WizTranslableProperty('translatedPlaceholder')// décorateur de méthode pour traduction du prompt
  @Input() public set wizPlaceholder(str: string) {
    this._wizPlaceholder = str;
  }
  public get wizPlaceholder(): string {
    return this._wizPlaceholder;
  }

  private _wizHint: string; // le hint, sera traduit par son wizLabel
  @Input() public set wizHint(v: string) {
    this.setWizHint(v);
  }
  public get wizHint(): string {
    return this.getWizHint();
  }
  /**
   * methodes getter/setter surchargeables du WizHint
   */
  protected setWizHint(value: string): void {
    this._wizHint = value;
    this.cdref.markForCheck();
  }
  protected getWizHint(): string {
    return this._wizHint;
  }

  /** Mode saisie manuelle */
  protected _manualInput: boolean = true;
  @Input()
  public get wizManualInput(): boolean {
    return this.getWizManualInput();
  }
  public set wizManualInput(val: boolean) {
    this.setWizManualInput(val);
  }
  /**
   * methodes getter/setter surchargeables du WizManualInput
   */
  protected setWizManualInput(value: boolean): void {
    this._manualInput = value;
    this.cdref.markForCheck();
  }
  protected getWizManualInput(): boolean {
    return this._manualInput;
  }

  /** Extra CSS class pour le date picker */
  private _wizExtraClassDatePicker: string = '';
  @Input()
  public set wizExtraClassDatePicker(cssClass: string) {
    this.setWizExtraClassDatePicker(cssClass);
  }
  public get wizExtraClassDatePicker(): string {
    return this.getWizExtraClassDatePicker();
  }
  public setWizExtraClassDatePicker(cssClass: string): void {
    this._wizExtraClassDatePicker = cssClass;
  }
  public getWizExtraClassDatePicker(): string {
    return this._wizExtraClassDatePicker;
  }

  // ####
  // ####
  // #### configuration spécifique dateField (calendar) ####
  // ####
  // ####

  /** Date minimale */
  protected _minDate: Date;
  /** Date maximale */
  protected _maxDate: Date;
  /** Dates sélectionnables  */
  protected _selectableDates: Date[] = [];
  /** Jours sélectionnables (dimanche = 0, lundi = 1, ..) */
  protected _selectableDays: number[] = [];
  /** Mois sélectionnables (Janvier = 0, ..) */
  protected _selectableMonths: number[] = [];
  /** Dates exclues de la sélection */
  protected _excludedDates: Date[] = [];
  /** Jours exclus de la sélection (dimanche = 0, lundi = 1, ..) */
  protected _excludedDays: number[] = [];
  /** Mois exclus de la sélection (Janvier = 0, ..) */
  protected _excludedMonths: number[] = [];

  /** Date maximale */
  @Input()
  public get wizMaxDate(): Date {
    return this.getWizMaxDate();
  }
  public set wizMaxDate(val: Date) {
    this.setWizMaxDate(val);
  }
  /**
   * methodes getter/setter surchargeables du WizMaxDate
   */
  protected getWizMaxDate(): Date {
    return this._maxDate;
  }
  protected setWizMaxDate(value: Date): void {
    this._maxDate = value;
    this.cdref.markForCheck();
  }

  /** Date minimale */
  @Input()
  public get wizMinDate(): Date {
    return this.getWizMinDate();
  }
  public set wizMinDate(val: Date) {
    this.setWizMinDate(val);
  }
  /**
   * methodes getter/setter surchargeables du WizMinDate
   */
  protected getWizMinDate(): Date {
    return this._minDate;
  }
  protected setWizMinDate(value: Date): void {
    this._minDate = value;
    this.cdref.markForCheck();
  }
  /** Dates sélectionnables  */
  @Input()
  public get wizSelectableDates(): Date[] {
    return this.getWizSelectableDates();
  }

  public set wizSelectableDates(val: Date[]) {
    this.setWizSelectableDates(val);
  }
  /**
   * methodes getter/setter surchargeables du WizSelectableDates
   */
  protected getWizSelectableDates(): Date[] {
    return this._selectableDates;
  }
  protected setWizSelectableDates(val: Date[]): void {
    this._selectableDates = val;
    this.cdref.markForCheck();
  }

  /** Jours sélectionnables (dimanche = 0, lundi = 1, ..) */
  @Input()
  public get wizSelectableDays(): number[] {
    return this.getWizSelectableDays();
  }

  public set wizSelectableDays(val: number[]) {
    this.setWizSelectableDays(val);
  }
  /**
   * methodes getter/setter surchargeables du WizSelectableDays
   */
  protected getWizSelectableDays(): number[] {
    return this._selectableDays;
  }
  protected setWizSelectableDays(value: number[]): void {
    this._selectableDays = value;
    this.cdref.markForCheck();
  }

  /** Mois sélectionnables (Janvier = 0, ..) */
  @Input()
  public get wizSelectableMonths(): number[] {
    return this.getWizSelectableMonths();
  }

  public set wizSelectableMonths(val: number[]) {
    this.setWizSelectableMonths(val);
  }
  /**
   * methodes getter/setter surchargeables du WizSelectableDays
   */
  protected getWizSelectableMonths(): number[] {
    return this._selectableMonths;
  }
  protected setWizSelectableMonths(value: number[]): void {
    this._selectableMonths = value;
    this.cdref.markForCheck();
  }

  /** Dates exclues de la sélection */
  @Input()
  public get wizExcludedDates():  Date[] {
    return this.getWizExcludedDates();
  }
  public set wizExcludedDates(val:  Date[]) {
    this.setWizExcludedDates(val);
  }
  /**
   * methodes getter/setter surchargeables du WizExcludedDates
   */
  protected getWizExcludedDates():  Date[] {
    return this._excludedDates;
  }
  protected setWizExcludedDates(value:  Date[]): void {
    this._excludedDates = value;
    this.cdref.markForCheck();
  }

  /** Jours exclus de la sélection (dimanche = 0, lundi = 1, ..) */
  @Input()
  public get wizExcludedDays(): number[] {
    return this.getWizExcludedDays();
  }
  public set wizExcludedDays(val: number[]) {
    this.setWizExcludedDays(val);
  }
  /**
   * methodes getter/setter surchargeables du WizExcludedDays
   */
  protected getWizExcludedDays(): number[] {
    return this._excludedDays;
  }
  protected setWizExcludedDays(value: number[]): void {
    this._excludedDays = value;
    this.cdref.markForCheck();
  }

  /** Mois exclus de la sélection (Janvier = 0, ..) */
  @Input()
  public get wizExcludedMonths() {
    return this._excludedMonths;
  }
  public set wizExcludedMonths(val: number[]) {
    this._excludedMonths = val;
    this.cdref.markForCheck();
  }
  /**
   * methodes getter/setter surchargeables du WizExcludedMonths
   */
  protected getWizExcludedMonths(): number[] {
    return this._excludedMonths;
  }
  protected setWizExcludedMonths(value: number[]): void {
    this._excludedMonths = value;
    this.cdref.markForCheck();
  }

  // ####
  // ####
  // #### mécanique interne ####
  // ####
  // ####

  /** flag signalant que la popUp est ouverte (pour maintient du skin "focus") */
  public popUpOpened: boolean = false;

  /** écouteur sur l'état de la popUp pour fakeFocus */
  public popOpened(): void {
    // console.log('popOpened',  this.datePickerForPopDeclaration['_popupComponentRef'].location);
    // ajout du picker comme un enfant
    WizFocusManager.getInstance().wizRegisterOwner(this, this.datePicker.matContent);

    this.popUpOpened = true;
    // suppression du message d'error à l'ouverture du calendar
    if (this._hasErrors) {
      this._hasErrors = false;
      this.manageErrorDisplay();
    }
  }

  /**
   * écouteur sur la fermeture de la pop
   */
  public popClosed(): void {
    this.popUpOpened = false;

    if (!this.wizManualInput) {// mode non manuel, set de la WizValue sur close Drop
      this.feedValueFromAutoMode();
    }

    // signaler une modif
    if (this.wizManualInput){ //  && !WizDateUtils.sameDates(this.getWizValue(), this._wizInitialValue)) { // changement
      
      if(this.dateCheck()) {
        setTimeout(() => {
          // this.prepareForCheck();
          // console.log('DF popClosed', WizDateUtils.dateToNumber(this.getDateFromInner()));
          // this.wizKey.emit(`${WizDateUtils.dateToNumber(this.getDateFromInner())}`);
          // console.log('DF popClosed', this.getDateFromInner());
          this.wizKey.emit(this.getDateFromInner());
        }, 0);
      }

    }

    this.innerInput.nativeElement.focus();
  }

  /** alimentation de la wizValue sur fermeture de pop / clear, UNIQUEMENT en mode "non-manuel" */
  private feedValueFromAutoMode(): void {

    // construction de la nouvelle date depuis l'input
    const newDate: Date = this.getDateFromInner();

    // comparaison
    if (!WizDateUtils.sameDates(newDate, this._wizInitialValue)) {
      // console.log ('feedValueFromAutoMode', this._wizInitialValue, newDate);
      this._wizInitialValue = null;// neutralisation _wizInitialValue pour réalimentation dans SetWizvalue()
      this.setWizValue(newDate);
      WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this.wizValue));
    }
  }

  /** sur prise de focus, mémorisation de la valeur pour dispatch si changement en sortie */
  public onFocus(e): void {
    if (!this._componentIsActive) {// annulée sur focusOut véritable
      this._componentIsActive = true;
      this._wizInitialValue = this.getWizValue();
    }

    if (e) {// c'est une prise de focus, pas un clic sur clear
      this.wizGetFocus();
    }
  }

  /** sur perte de focus, gestion de l'interne (pop) et de la validation */
  public onBlur(e): void {

    // fausse sortie, je reviens de la popUp
    if (WizFocusManager.getInstance().isCurrentFocused(e.target)) {

      // console.log ('df onBlur notloose'+e.target);
      e.stopImmediatePropagation();
      e.preventDefault();
      // console.log('wizOnBlur prevent',e, WizFocusManager.getInstance().isCurrentFocused(e.target));
    } else {

      // supprimer l'indicateur de focus (multi)
      this.popUpOpened = false;

      if (this.wizManualInput && !WizDateUtils.sameDates(this.getWizValue(), this._wizInitialValue)) { // changement
        // console.log ('dateChange '+this.wizLabel , this._wizInitialValue, this.wizValue);
        WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this.wizValue));
      }

      // console.log ('df onBlur loose', e, this.getWizValue(), this.wizValue);

      if(this.wizInputValue && !this.wizValue) {
        this.innerInput.nativeElement.value = null;
      }
      // console.log ('df onBlur loose2', e, this.getWizValue(), this.wizValue);
      this._wizInitialValue = null;
      this._componentIsActive = false;
      // console.log('wizOnBlur wizLoseFocus '+ this.wizLabel,this.getWizValue(), this._wizInitialValue);
      this.wizLoseFocus();
    }
  }

  /** accesseur sur la valeur actuelle du composant de saisie */
  private get wizInputValue(): string {
    // console.log('wizInputValue', this.innerInput.nativeElement.value);
    return this.innerInput ? this.innerInput.nativeElement.value : null;
  }

  /** Clear de la date, clic sur l'icone */
  public clearDate(e: MouseEvent) {
    if (!this.wizDisabled) {
      // stopper l'event, pour ne pas appeler la popUp
      e.stopImmediatePropagation();
      e.preventDefault();


      this.innerInput.nativeElement.value = ''; // reset sur l'élément pour neutraliser le mode erreur dans setWizValue
      
      // alimentation du wizInitialValue si nécessaire
      // permet de sauvegarder l'initial value si elle est n'est pas setter
      this.onFocus(null);

      this.setWizValue(null);
      // this.setLastGoodValue(null);
      
      // en mode non éditable, dispatch éventuel de l'event
      if (!this.wizManualInput) {
        this.feedValueFromAutoMode();
      }

      WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this.wizValue));

      // forcer le retour de focus vers l'input
      // ou pas this.innerInput.nativeElement.focus();
    }
  }

  /**
   * Handler de clic dans l'input, simuler le clic sur BTCalendar (en mode !wizManualInput)
   * @param event Evenement clic
   */
  public inputClic(event: MouseEvent) {
    // console.log('inputClic',event);
    if (!this.wizManualInput) {
      // this.datePicker.open();
      this.datepickerToggle._open(event); // on utilise la methode open du toggle pour donner le focus au calendar à l'ouverture
    }
  }

  /**
   * Ouverture clavier du datepicker
   * @param event Evenement clavier
   */
  public onKeyDown(event: KeyboardEvent) {
    // ouverture sur up / down / space
    if (event.key === VALUE_DOWN || event.key === VALUE_UP || event.key === VALUE_SPACE) {
      if (!this.datePicker.opened) {
        this.datePicker.datepickerInput.dateFilter = this.dateFilter;
        this.datePicker.open();
      }
    }
  }

  /**
   * Restriction de saisie dans l'input
   * keypress deprecié, mais seul évènement faisant le job, beforeinsert n'étant pas dispatché dans firefox
   * @param event Evenement clavier
   */
  public onKeyPress(event: KeyboardEvent) {
    if (this._hasErrors) { // suppression éventuelle de l'affichage d'erreur
      this._hasErrors = false;
      this.manageErrorDisplay();
    }

    // Les caractères spéciaux ne génèrent pas de keypress
    this.preventInsert(event.key, event);
  }

  /**
   * appeler lors d'un coller dans l'input
   * cette méthode récupère le texte coller et appelle la méthode wizPreventInsert
   * On écoute l'évenement paste en plus du keydown ctrl + v || shift + Inser, car un coller peut être déclenché par la souris
   * @param event, Paste event
   */
  private onPaste(event: ClipboardEvent) {
    // le caractère en cours
    const inputChar = event.clipboardData.getData('Text');
    this.preventInsert(inputChar, event);
  }

  /**
   * annule l'insertion d'un caractère ou chaine de caractère
   *  si cette valeur est invalide par rapport à dateRegExp
   * @params value, le caratère ou la chaine à inserer
   * @params event, l'event à prevent si value n'est pas autorisé
   */
  private preventInsert(value: string, event: Event) {
    // La chaine existante
    const currentString: string = this.wizInputValue;
    // la future chaine
    const selectionStart: number = this.innerInput.nativeElement.selectionStart;
    const selectionEnd: number = this.innerInput.nativeElement.selectionEnd;
    const next: string = currentString.substr(0, selectionStart) + value + currentString.substr(selectionEnd);

    // controle sur base de la regExp
    if (next && !String(next).match(this._dateRegExp)) {
      event.preventDefault();
      return;
    }


    if (this.wizManualInput){ //  && !WizDateUtils.sameDates(this.getWizValue(), this._wizInitialValue)) { // changement
      // console.log ('dateChange '+this.wizLabel , this._wizInitialValue, this.wizValue);
      //if(this.dateCheck()) {
        // this.prepareForCheck();
        setTimeout(() => {
          // console.log('DF preventInsert',this.wizInputValue, this.getDateFromInner());
          this.wizKey.emit(this.getDateFromInner());
          // console.log('DF preventInsert', this.getDateFromInner());
          // this.wizKey.emit(`${this.getDateFromInner()}`);
        }, 0);
        // this.wizKey.emit(`${this.getDateFromInner()}`);
      //}
      // this.wizKey.emit(`${this.wizValue}`);
    }

  }

  // sur wizValidation, alimentation du wizValue avec le prototype en saisie
  public prepareForCheck(): void {
    this.setWizValue(this.getDateFromInner());
  }

  /**
   * construction d'une date depuis la saisie en cours
   * utilisé avant focusOut pour setter WizValue (pour cycle de validation)
   */
  private getDateFromInner(): Date {
    // on prend le string en cours pour en faire une date
    const dateStr: string = this.wizInputValue;

    // on essaye de transformer en date
    const date: Date = (this.adapter as WizNativeDateAdapter).parse(dateStr);
    //console.log('dateCheck',dateStr, date, this.getWizValue());
    return date;
  }

  /**
   * Test de la validité d'une date
   * - Définie si mandatory
   * - Pas dans les éléments exclus de la sélection, inclue dans les tableaux d'inclusion définis
   * - entre wizMinDate et wizMaxDate si définis
   * ... et c'est une date..
   */
  private dateCheck(): boolean {

    const date: Date = this.getDateFromInner();
    // TODO override du isvalid sur adapter pour utiliser luxon / trap nullPointer
    if (date && !this.adapter.isValid(date)) { // cast impossible, erreur de format
      // console.log('invalid Date');
      return false;
    }

    if (this.wizMandatory && !date) { // pas de date sur mandatory
      return false;
    }

    if (!this.dateFilter(date)) { // pas dans les bornes de jours / mois tolérés
      return false;
    }

    if (this.wizMinDate && WizDateUtils.isBefore(date, this.wizMinDate)) { // trop ancien
      return false;
    }

    if (this.wizMaxDate && WizDateUtils.isAfter(date, this.wizMaxDate)) { // trop nouveau
      return false;
    }

    return true;
  }

  /** affichage de l'icone clear : si souhaité, pas disabled, et donnée */
  public showClearIcon(): boolean {
    let show: boolean = this.wizShowClearButton && !!this.wizInputValue && !this.wizDisabled;
    
    return show;
  }

  /** texte readOnly */
  public getReadOnly(): boolean {
    return !this.wizManualInput;
  }

  /** Style du pointeur de souris sur l'input, selon édition / disabled */
  public getInputPointer(): string {
    return this.wizManualInput || this.wizDisabled ? '' : 'pointer';
  }

  /** détermination et application de l'état */
  private applyCheck(): void {
    this.manageErrorDisplay(); // refresh visuel
  }

  // ####
  // ####
  // #### gestion de la valeur et des erreurs ####
  // ####
  // ####/

  protected _componentIsActive: boolean = false; // le focus est pris (car initialValue peut être null)
  protected _wizInitialValue: Date = null; // initiale , alimentée sur prise de focus

  private _hasErrors: boolean = false; // le composant a à minima une erreur
  public get hasErrors(): boolean {
    return this._hasErrors;
  }

  /** contrat d'interface. une DATE. C'est le SEUL objet géré en interne */
  public setWizValue(value: any) {
    if (!this._lastGoodValue) { // alimentation initiale de la lastGood
      this._lastGoodValue = value;
    }
    super.setWizValue(value);
    // console.log('setWizValue '+  value, this.wizInputValue);

    if (!this._componentIsActive) {
      // check wizInitialValue pour ne pas afficher d'erreur quand la valeur est setter par programmation
      this._hasErrors = false;
      if(this.wizInputValue && !value) {
        this.innerInput.nativeElement.value = null;
      }

    } else if (this.wizInputValue && !value) {// le texte de l'input n'est pas vide et date =null => erreur
      this._hasErrors = true;
    } else if (this.wizMandatory && !value) {// pas de valeur et mandatory
      this._hasErrors = true;
    } else {
      this._hasErrors = false;
    }

    this.applyCheck();
  }

  // public getWizValue(): any {
  //   let res = super.getWizValue();
  //   console.log('dateField getWizValue', res);
  //   return res;
  // }



  /** gestion de la date au format number YYYYMMDD */
  @Input() public set wizNumberValue(val: number) {
    this.setWizNumberValue(val);
  }
  public get wizNumberValue() {
    return this.getWizNumberValue();
  }
  /**
   * methodes getter/setter surchargeables du WizNumberValue
   */
  protected setWizNumberValue(value: number) {
    const date: Date = WizDateUtils.numberToDate(value);
    // neutralisation de la dernière bonne valeur
    this._lastGoodValue = null;
    this.setWizValue(date);
  }
  protected getWizNumberValue(): number {
    return WizDateUtils.dateToNumber(this.getWizValue());
  }

  /** gestion de la date au format date */
  @Input()
  public set wizDateValue(value: Date) {
    this.setWizDateValue(value);
  }
  public get wizDateValue(): Date {
    return this.getWizDateValue();
  }
  /**
   * methodes getter/setter surchargeables du WizDateValue
   */
  protected setWizDateValue(value: Date): void {
    // neutralisation de la dernière bonne valeur
    this._lastGoodValue = null;
    // neutralisation des heures
    this.setWizValue(WizDateUtils.clearTime(value));
  }
  protected getWizDateValue(): Date {
    return this.getWizValue();
  }
  /**
   * Appel externe sur focusOut depuis la directive wizValidator
   * la mise à jour des données est réalisée par dateCheck
   * @param pErrors la collection d'erreur
   * @param wizShowErrors, boolean d'affichage ou non du message d'erreur
   */
  public onWizValidation(pErrors: WizValidationErrors, wizShowErrors: boolean): void {
    // console.log('onWizValidation '+ this.wizLabel ,pErrors.length);
    this._hasErrors = pErrors.length > 0;
    this.manageErrorDisplay();
  }

  /** mode focus.rollback, retour dernière bonne config */
  public wizRollback(): void {
    setTimeout(() => { // sinon trop tôt, pas modifié
      // console.log('wizRollback '+ this.wizLabel ,this.wizValue,this._lastGoodValue, this.wizInputValue);
      this.innerInput.nativeElement.value = this.wizValue ? this.adapter.format(this.wizValue, null) : null;
    }, 0);

  }

  /** suppression / affichage des marqueurs d'erreur & messages */
  protected manageErrorDisplay(): void {
    if (this._hasErrors) {
      // TODO marche pas 1er focus Out
    }
    else {
      if (this.getErrorVCRef()) {// clear container
        this.getErrorVCRef().clear();
      }
    }
    this.cdref.markForCheck();
  }

  /** Error message container : contrat d'interface pour la directive wizValidator */
  public getErrorVCRef(): ViewContainerRef {
    return this.errorContainer;
  }

  // ####
  // ####
  // #### contraintes ####
  // ####
  // ####/

  /** Clé de trad pour le message d'erreur mandatory */
  @Input() public set wizRequiredErrorMessage(str: string) {
    this.setWizRequiredErrorMessage(str);
  }
  public get wizRequiredErrorMessage(): string {
    return this.getWizRequiredErrorMessage();
  }
  private _wizRequiredErrorMessage: string;
  /**
   * methodes getter/setter surchargeables du wizRequiredErrorMessage
   */
  protected setWizRequiredErrorMessage(str: string): void {
    this._wizRequiredErrorMessage = str;
    this.cdref.markForCheck();
  }
  protected getWizRequiredErrorMessage(): string {
    return this._wizRequiredErrorMessage;
  }

  /** Clé de trad pour le message d'erreur date invalide */
  @Input() public set wizInvalidErrorMessage(str: string) {
    this.setWizInvalidErrorMessage(str);
  }
  public get wizInvalidErrorMessage(): string {
    return this.getWizInvalidErrorMessage();
  }
  private _wizInvalidErrorMessage: string;
  /**
   * methodes getter/setter surchargeables du _wizInvalidErrorMessage
   */
  protected setWizInvalidErrorMessage(str: string): void {
    this._wizInvalidErrorMessage = str;
    this.cdref.markForCheck();
  }
  protected getWizInvalidErrorMessage(): string {
    return this._wizInvalidErrorMessage;
  }
  /**
   * initialisation des méthodes de validation
   */
  public getValidators() {
    const validators: WizValidatorFn[] = [];

    // this.prepareForCheck();

    let invalidDateMessage = '';
    // if wizMandatory is true, the required validator in added to wizValidatorsArray
    if (this.wizMandatory) {
      const errorMessage = this.wizRequiredErrorMessage ? this.wizRequiredErrorMessage : WIZ_ERROR_MESSAGE.REQUIRED;
      validators.push(WizValidators.wizRequired(errorMessage));
    }

    invalidDateMessage = this.wizInvalidErrorMessage ? this.wizInvalidErrorMessage : WIZ_ERROR_MESSAGE.INVALID_DATE;
    // vrai validateur du DateField. Appel à DateCheck
    validators.push(this.wizValidDate(invalidDateMessage));

    return validators;
  }

  /**
   * Méthode de validation de la date saisie (check du WizErrorManager)
   * @param errorMessage, Le message d'erreur
   */
  private wizValidDate(errorMessage: string): WizValidatorFn {
    return (compo: WizDateField): WizValidationErrors | null => {
      const temp: WizValidationError = {
        wizError: errorMessage
      };
      return (this.dateCheck()) ? null : [temp];
    };
  }

  /**
   * Fonction de filtre des dates sélectionnables
   * dans le calendar
   *
   * @param pDate Date testée
   * @return boolean résultat du test
   * TODO: déplacer ce code dans la classe util
   */
  public dateFilter = (pDate: Date): boolean => {
    if (pDate) {
      return this.isDaySelectable(pDate)
        && this.isMonthSelectable(pDate)
        && this.isDateSelectable(pDate);
    }
    return true;
  }

  /**
   * Check si un jour est sélectionnable
   * @param pDate Date testée
   * @return boolean résultat du test
   */
  public isDaySelectable(pDate: Date): boolean {
    const day = pDate.getDay();
    // jour inclus, mais pas dedans
    if (this.wizSelectableDays.length > 0 && !this.wizSelectableDays.includes(day)) {
      return false;
    }
    if (this.wizExcludedDays.includes(day)) { // jour exclus
      return false;
    }
    return true;
  }

  /**
   * Check si un mois est sélectionnable
   * @param pDate Date testée
   * @return boolean résultat du test
   */
  public isMonthSelectable(pDate: Date): boolean {
    const month = pDate.getMonth();
    // restrictions sur tableau de jours, et aucune date pour ce mois
    if (this.wizSelectableDates.length > 0 &&
      this.wizSelectableDates.find((d: Date) => d.getMonth() === month) === undefined) {
      return false;
    }
    // mois inclus, mais pas dedans
    if (this.wizSelectableMonths.length > 0 && !this.wizSelectableMonths.includes(month)) {
      return false;
    }
    if (this.wizExcludedMonths.includes(month)) { // mois exclus
      return false;
    }
    return true;
  }

  /**
   * Check si une date est sélectionnable
   * @param pDate Date testée
   * @return boolean résultat du test
   */
  public isDateSelectable(pDate: Date): boolean {
    const excludedDateslength = this.wizExcludedDates.length;
    const selectableDateslength = this.wizSelectableDates.length;
    // Usage of [for] instead of [for..of/filter/find] because it's faster

    // Dans les dates incluses si définies
    if (selectableDateslength > 0) {
      for (let i = 0; i < selectableDateslength; i++) {
        const selectableDate = new Date(this.wizSelectableDates[i]);
        if (WizDateUtils.isSame(selectableDate, pDate)) {
          return true;
        }
      }
      return false;
    }

    // Pas dans les dates exclues
    for (let i = 0; i < excludedDateslength; i++) {
      const excludedDate = this.wizExcludedDates[i];
      if (WizDateUtils.isSame(excludedDate, pDate)) {
        return false;
      }
    }
    return true;
  }

  // ####
  // ####
  // #### gestion de la traduction ####
  // ####/

  /**
   * override de la traduction pour alimenter la locale du composant
   * @param compTrsVOList component translations array
   */
  public wizTranslate(compTrsVOList: WizComponentTranslationObj[]): void {
    super.wizTranslate(compTrsVOList);
    this.updateDateRegexp();
  }

  /** RegExp pour contrôle de saisie, modifié sur changement de langue */
  private updateDateRegexp(): void {
    let dateSeparator: string = WizStringUtils.escapeForRegexp(WizDateUtils.getLocaleDateSeparator(WizTranslationManager.getInstance().dateLocale));
    const regexStr: string = '(^\\d{1,4}'+dateSeparator+'{0,1}$)|(^\\d{1,4}'+dateSeparator+'{0,1}\\d{1,2}'+dateSeparator+'{0,1}\\d{0,4}$)';
    this._dateRegExp = new RegExp(regexStr, 'g');
  }


  // ####
  // ####
  // #### gestion du style ####
  // ####/


  /** skinning */
  // public hoverClassName: string = 'hover-class'; // class css pour survol
  // public disabledDateClassName: string = 'disabled-class'; // class css pour skin "disabled"
  public todayDateClassName: string = 'today-class'; // class css pour skin cellule "today"
  public selectedDateClassName: string = 'selected-class'; // class css la cellule selectionnée

  public dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    // Only highligh dates inside the month view.
    if (view === 'month') {
      const date = cellDate.getDate();
      if (WizDateUtils.isSame(WizDateUtils.TODAY, cellDate)) { // TODO if date is today
        return this.todayDateClassName;
      }
      // Todo handle disabled dates
      // if (!this.isDateSelectable(cellDate)) { // lorsque la date de la cellule est disable
      //   return this.disabledDateClassName;
      // }
    }
  }

  /**
   * Sélection d'année
   * @param year Date (1/1/année sélectionnée)
   */
  public chosenYearHandler(year: Date) {
    if (this._wizDateMode === WizDateUtils.YEAR_MODE) {
      this.setWizDateValue(year)
      this.datePicker.close();
    }
  }

  /**
   * Sélection de mois
   * @param year Date (1/1/année sélectionnée)
   */
  chosenMonthHandler(month: Date) {
    if (this._wizDateMode === WizDateUtils.MONTH_MODE) {
      this.setWizDateValue(month)
      this.datePicker.close();
    }
  }

  /** donne le focus à l'input depuis FocusManager */
  public wizFocus(): void {
    if (this.wizManualInput) {
      this.innerInput.nativeElement.select(); // select pour selectionner le contenu de l'input lors du focus
    }
  }

  /**
   * donne le focus à l'input depuis FocusManager
   *
   * @param value valeur à injecter
   */
  public wizFocusInjector(value: string): void {
    if (this.wizManualInput) {
      // controle sur base de la regExp
      if (value && value.match(this._dateRegExp)) {
        this.innerInput.nativeElement.select(); // select pour selectionner le contenu de l'input lors du focus
        this.innerInput.nativeElement.value = value;
        // console.log('wizFocusWithValue', this.innerInput.nativeElement.value);
      } else {
        this.wizFocus();
        // console.log('wizFocusWithValue nope', this.innerInput.nativeElement.value);
      }
    }
  }
}
